import { ListIntegrationFragment } from 'generated/resources/graphql'

import integrationCodes from 'modules/marketplace/lib/integrationCodes'

export interface Category {
  id: string
  title: string
  description: string
  children: ListIntegrationFragment[]
}

export class IntegrationsCatalogSorter {
  installedIntegrations: string[]
  searchValue: string
  installedFilter: boolean
  countryFilter: string[]
  categoryFilter: string[]
  categories: Category[]

  constructor() {
    this.installedIntegrations = []
    this.searchValue = ''
    this.installedFilter = false
    this.countryFilter = []
    this.categoryFilter = []
    this.categories = []
  }

  public filterCategories() {
    return this.categories
      .filter((category) => this.filterByCategoryMultipleChoiceFilter(category))
      .reduce((accumulator: Category[], category) => {
        const integrations = category.children.filter((integration) =>
          this.isIntegrationVisible(integration)
        )
        if (integrations.length === 0) return accumulator
        return [
          ...accumulator,
          {
            ...category,
            children: integrations,
          },
        ]
      }, [])
  }

  public setInstalledFilter(value: boolean) {
    this.installedFilter = value
  }

  public setCountryFilter(value: string[]) {
    this.countryFilter = value
  }

  public setCategoryFilter(value: string[]) {
    this.categoryFilter = value
  }

  public setSearchValue(value: string) {
    this.searchValue = value
  }

  public setCategories(categories: Category[]) {
    this.categories = this.sortCategoriesByRelevance(categories)
  }

  public setInstalledIntegrations(value: string[]) {
    this.installedIntegrations = value
  }

  private isIntegrationVisible(integration: ListIntegrationFragment) {
    const isInstalled = this.installedIntegrations.some(
      (installedIntegration) => installedIntegration === integration.id
    )

    return (
      this.integrationMatchesSearchedValue(integration) &&
      this.integrationMatchesInstalledFilter(isInstalled) &&
      this.integrationMatchesCountryFilter(integration) &&
      this.isOldDatevIntegrationVisible(integration, isInstalled)
    )
  }

  private filterByCategoryMultipleChoiceFilter(category: Category) {
    return this.categoryFilter.length > 0 ? this.categoryFilter.includes(category.id) : true
  }

  private sortCategoriesByRelevance(categories: Category[]) {
    const PAYROLL_GROUP_ID = '146390751'
    const ERP_GROUP_ID = 'LG8YUFVAR5ekjOyPCdTUjA'
    const ACCESS_AND_SPACE_GROUP_ID = '146532919'

    const SPECIAL_GROUPS_ORDER = [PAYROLL_GROUP_ID, ERP_GROUP_ID, ACCESS_AND_SPACE_GROUP_ID]

    return Object.values(categories).sort((groupA, groupB) => {
      const indexA = SPECIAL_GROUPS_ORDER.indexOf(groupA?.id)
      const indexB = SPECIAL_GROUPS_ORDER.indexOf(groupB?.id)

      if (indexA !== -1 && indexB !== -1) return indexA - indexB
      if (indexA !== -1) return -1
      if (indexB !== -1) return 1

      return groupA?.title?.toLowerCase().localeCompare(groupB?.title?.toLowerCase())
    })
  }

  private integrationMatchesInstalledFilter(isIntegrationInstalled: boolean) {
    if (!this.installedFilter) return true
    return isIntegrationInstalled
  }

  private integrationMatchesSearchedValue(integration: ListIntegrationFragment) {
    const integrationName = integration.name?.toLowerCase()
    const searchText = this.searchValue.toLowerCase()
    return integrationName.includes(searchText)
  }

  private integrationMatchesCountryFilter(integration: ListIntegrationFragment) {
    const GLOBAL_COUNTRY_ID = '146392520'
    if (!integration?.countries || this.countryFilter.length == 0) return true
    return integration.countries.some(
      (country) => this.countryFilter.includes(country.id) || country.id === GLOBAL_COUNTRY_ID
    )
  }

  private isOldDatevIntegrationVisible = (
    integration: ListIntegrationFragment,
    isIntegrationInstalled: boolean
  ) => {
    const isOldDatevIntegration =
      integration.datoId === integrationCodes.datev_api ||
      integration.datoId === integrationCodes.datev_lug_api

    if (!isOldDatevIntegration) return true

    return isIntegrationInstalled
  }
}
